import { useCallback } from "react"

import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { Button, Card, Header, Icon, Message } from "semantic-ui-react"

import { UserChip } from "components/lib/Chips"
import { useAppSelector } from "store"
import util from "utils/utils"

import type { InviteCardProps } from "./types"

const RequestJoinIdeaInviteCard = ({
  invitation,
  acceptInvite,
  rejectInvite,
  removeInvite,
  cardView = true,
}: InviteCardProps) => {
  const { inviterUser, idea, createdByCurrentUser } = invitation
  const user = useAppSelector((state) => state.user)
  const { t } = useTranslation()

  const accept = useCallback(() => {
    util
      .confirm(
        `Allow ${t("generic.idea")} join`,
        `Are you sure you want to allow ${inviterUser?.profile?.fullName} to join "${idea?.name}"?`,
      )
      .then(() => {
        acceptInvite(
          invitation,
          () => {
            toast.success("Accepted request")
          },
          () => toast.error("Error while accepting request"),
        )
      })
      .catch(() => {})
  }, [acceptInvite, invitation, idea?.name, inviterUser?.profile?.fullName, t])

  const reject = useCallback(() => {
    util
      .confirm(
        `Reject ${t("generic.idea")} join`,
        `Are you sure you want to reject ${inviterUser?.profile?.fullName} from joining "${idea?.name || `Deleted ${t("generic.idea")}`}"?`,
      )
      .then(() => {
        rejectInvite(
          invitation,
          () => {
            toast("Request rejected")
          },
          () => toast.error("Error rejecting request"),
        )
      })
      .catch(() => {})
  }, [rejectInvite, invitation, idea?.name, inviterUser?.profile?.fullName, t])

  const remove = useCallback(() => {
    util
      .confirm(
        "Remove request",
        `Are you sure you want to remove your request to join "${idea?.name || `Deleted ${t("generic.idea")}`}"?`,
      )
      .then(() => {
        removeInvite(
          invitation,
          () => {
            toast("Invitation removed")
          },
          () => toast.error("Error removing invite"),
        )
      })
      .catch(() => {})
  }, [removeInvite, invitation, idea?.name, t])

  if (!idea) {
    invitation.canAccept = false
  }

  return (
    <div style={cardView ? { display: "flex", justifyContent: "space-between", alignItems: "center" } : {}}>
      <div>
        {cardView ? (
          <Header style={{ marginBottom: "0.3em" }} as={Link} disabled={!idea} to={idea ? `/ideas/${idea._id}` : null}>
            {idea?.name || `Deleted ${t("generic.idea")}`}
          </Header>
        ) : (
          <Header as="h2" icon textAlign="center">
            <Icon name="users" circular />
            <Header.Content>Request to join {idea?.name || `Deleted ${t("generic.idea")}`}</Header.Content>
          </Header>
        )}
        <Card.Description>
          {createdByCurrentUser ? (
            `You have requested to join this ${t("generic.idea")}`
          ) : (
            <UserChip compact="very" user={inviterUser} postText={`has requested to join this ${t("generic.idea")}`} />
          )}
        </Card.Description>
      </div>
      <Card.Content extra>
        <div className="ui" style={cardView ? {} : { marginTop: 20, display: "flex", justifyContent: "flex-end" }}>
          {invitation.acceptedAt && idea && (
            <Button basic color="green" as={Link} to={`/ideas/${idea?._id}`}>
              Go to {t("generic.idea")}
            </Button>
          )}
          {!invitation.acceptedAt && (
            <>
              {!idea && (
                <Message error style={{ width: "100%" }}>
                  This request to join is no longer valid because the {t("generic.idea")} has been deleted.
                </Message>
              )}
              {user && invitation.canRemove ? (
                <Button
                  basic
                  color={createdByCurrentUser ? null : "red"}
                  onClick={createdByCurrentUser ? remove : reject}
                >
                  {createdByCurrentUser ? "Remove" : "Reject"}
                </Button>
              ) : null}
              {user && invitation.canAccept ? (
                <Button basic color="green" onClick={accept}>
                  Accept
                </Button>
              ) : null}
            </>
          )}
        </div>
      </Card.Content>
    </div>
  )
}

export default RequestJoinIdeaInviteCard
